import React, { FC } from "react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import Typhography from "../../../shared/Typhography/Typhography";
import style from "../ProfilingProtocolDetailPage.module.scss";

interface IProfileTrendChartProps {
  profiles: any[];
  hubId?: string;
}

const TICK_STYLE = {
  fill: "#4d4d4d",
  fontSize: 11,
  fontFamily: "'Satoshi', sans-serif",
};

/** Tooltip con stili SCSS-compatibili — usa CSS vars tema (spec sez.5.1). */
const CustomTooltipContent: FC<any> = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className={style.trendTooltip}>
      <div className={style.trendTooltipLabel}>{label}</div>
      {payload.map((p: any) => (
        <div key={p.dataKey}>
          <strong>{p.name}:</strong> {p.value}
        </div>
      ))}
    </div>
  );
};

/**
 * Mappa protocollo → KPI primaria da tracciare nel trend (spec sez.5.1).
 * P1/P2: estimated1RM (VBT regression)
 * P3/P3b: FVimb (F-V salto — squilibrio dominante)
 * P4: FVimb (F-V sprint)
 * P5: estimated1RM (regression — sled o simile)
 * P6: peakRSI (drop jump)
 * P7: LT2 (soglia lattato/ventilatoria 2)
 * P8: asymmetryPct (Nordic — asimmetria forza ischio)
 * P9: VIFT (velocità intermittent fitness test)
 * P10: DSI (dynamic strength index)
 * P11–P14: index generici → valore primario "score" o primo output numerico
 */
const PROTOCOL_KPI: Record<string, { dataKey: string; name: string }> = {
  P1:  { dataKey: "estimated1RM", name: "1RM stimato (kg)" },
  P2:  { dataKey: "estimated1RM", name: "1RM stimato (kg)" },
  P3:  { dataKey: "FVimb",        name: "FV imbalance (%)" },
  P3b: { dataKey: "FVimb",        name: "FV imbalance (%)" },
  P4:  { dataKey: "FVimb",        name: "FV imbalance sprint (%)" },
  P5:  { dataKey: "estimated1RM", name: "1RM stimato (kg)" },
  P6:  { dataKey: "peakRSI",      name: "Peak RSI" },
  P7:  { dataKey: "LT2",          name: "LT2 (km/h)" },
  P8:  { dataKey: "asymmetryPct", name: "Asimmetria (%)" },
  P9:  { dataKey: "VIFT",         name: "VIFT (km/h)" },
  P10: { dataKey: "DSI",          name: "DSI" },
};

/** Ritorna la config KPI per il protocollo, o null se non mappato. */
function getKpiConfig(hubId?: string): { dataKey: string; name: string } | null {
  if (!hubId) return null;
  return PROTOCOL_KPI[hubId] ?? null;
}

export const ProfileTrendChart: FC<IProfileTrendChartProps> = ({
  profiles,
  hubId,
}) => {
  const kpi = getKpiConfig(hubId);

  const chartData = [...profiles]
    .reverse()
    .map((p: any) => ({
      date: new Date(p.sessionDate).toLocaleDateString("it-IT", {
        day: "2-digit",
        month: "2-digit",
      }),
      ...p.outputs,
    }));

  return (
    <div className={style.card}>
      <div className={style.cardHeader}>
        <Typhography size="bold-16" style={{ color: "#00212c", margin: 0 }}>
          Trend nel tempo
          {kpi && (
            <span className={style.trendKpiLabel}> — {kpi.name}</span>
          )}
        </Typhography>
      </div>

      <div className={style.trendWrapper}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={chartData}
            margin={{ top: 10, right: 30, bottom: 10, left: 10 }}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              stroke="#e7e7e7"
              vertical={false}
            />
            <XAxis
              dataKey="date"
              tick={TICK_STYLE}
              tickLine={false}
              axisLine={false}
            />
            <YAxis
              tick={TICK_STYLE}
              tickLine={false}
              axisLine={false}
            />
            <Tooltip content={<CustomTooltipContent />} />

            {kpi && (
              <Line
                type="monotone"
                dataKey={kpi.dataKey}
                name={kpi.name}
                stroke="#063444"
                strokeWidth={2}
                dot={{ fill: "#063444", r: 4, strokeWidth: 0 }}
                activeDot={{ r: 6, fill: "#063444" }}
                connectNulls
              />
            )}

            {/* Fallback: se hubId non è in mappa, non disegna nulla */}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};
